import { useState } from 'react';
import { useFormContext, useFormState } from "react-hook-form";
import { useNavigate } from "react-router";
import { Bounce, toast } from 'react-toastify';
import { useAuthStore } from "../../../store/authStore";
import { useAccountFormStore } from "../../../store/accountFormStore";
import { submitAccountBatchJob, fetchGetApiRequestJobStatus, submitV2Audit } from "../../../utils/apiUtils";
import { delay } from "../../../utils/commonUtils";
import { ACTIONS } from "../../../utils/constants";

const step = 6;
const TopAccountStepSix = ({ activeStep }) => {
    const navigate = useNavigate();

    const { control, getValues } = useFormContext();
    const { errors, isValid } = useFormState({ control });

    const { email } = useAuthStore();
    const { accounts, removeAccountByAccountId } = useAccountFormStore();

    const [isSubmitting, setIsSubmitting] = useState(false);

    if (activeStep !== step) return null;

    const draft = getValues();

    const getDateString = (date) => {
        if (date) {
            if (typeof date === 'string') {
                return date.substring(0, 10);
            } else if (date instanceof Date) {
                return date.toISOString().substring(0, 10);
            }
        }
        return '';
    };

    const convertDateStrToUDTDateStr = (dateStr) => {
        const [year, month, day] = dateStr.split("-");
        return `${month}/${day}/${year}`;
    };

    const onSubmitAccount = async () => {
        setIsSubmitting(true);

        const allAccountIdentifiers = accounts.reduce((acc, account) => {
            const topAccountIds = account.accountId === draft.accountId ? [] : [account.accountName, account.accountHccId];
            const subAccountIds = account.subAccounts?.flatMap(sa => [sa.accountName, sa.accountHccId]) ?? [];
            return [...acc, ...topAccountIds, ...subAccountIds];
        }, []);

        if (allAccountIdentifiers.some(id => id === draft.accountName)) {
            toast.error('Account Name already exists', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
                transition: Bounce,
            });
            setIsSubmitting(false);
            return;
        }

        if (allAccountIdentifiers.some(id => id === draft.accountHccId)) {
            toast.error('Account HCC ID already exists', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
                transition: Bounce,
            });
            setIsSubmitting(false);
            return;
        }

        const isCreateAction = draft.action === ACTIONS.CREATE;

        const bodies = [draft, ...draft.subAccounts].map((account) => {
            let udts = [];
            if (account.udts?.enableInvoiceInformation) {
                udts.push({
                    udtListValueSet: [
                        {
                            attributeRoleName: "Invoice Type",
                            attrValueAsString: account.udts.invoiceType?.value ?? '',
                        },
                        {
                            attributeRoleName: "Payment Remit Info",
                            attrValueAsString: account.udts.paymentRemit?.value ?? '',
                        },
                        {
                            attributeRoleName: "Purchase Order Id",
                            attrValueAsString: account.udts.purchaseOrder ?? '',
                        },
                    ],
                    userDefinedTermReference: {
                        ID: "Invoice Information",
                    },
                });
            }
            if (account.udts?.enableFundingMode) {
                udts.push({
                    udtListValueSet: [
                        {
                            attributeRoleName: "Funding Mode",
                            attrValueAsString: account.udts.fundingMode?.value ?? '',
                        },
                    ],
                    userDefinedTermReference: {
                        ID: "Funding Mode",
                    },
                });
            }
            if (account.udts?.enableAccountMigrationDate) {
                udts.push({
                    udtListValueSet: [
                        {
                            attributeRoleName: "Account Migration Date",
                            attrValueAsString: convertDateStrToUDTDateStr(account.udts.accountMigrationDate),
                        },
                    ],
                    userDefinedTermReference: {
                        ID: "Account Migration Date",
                    },
                });
            }
            if (account.udts?.enableNysHcra) {
                udts.push({
                    udtListValueSet: [
                        {
                            attributeRoleName: "New York State HCRA Election",
                            attrValueAsString: account.udts.nysHcra?.value ?? '',
                        },
                        {
                            attributeRoleName: "New York State HCRA Election Date",
                            attrValueAsString: convertDateStrToUDTDateStr(account.udts.nysHcraDate),
                        },
                        {
                            attributeRoleName: "New York State HCRA Election Notes",
                            attrValueAsString: account.udts.nysHcraNotes ?? '',
                        },
                    ],
                    userDefinedTermReference: {
                        ID: "New York State HCRA Election",
                    },
                });
            }
            if (account.udts?.enableRiskSharing) {
                udts.push({
                    udtListValueSet: [
                        {
                            attributeRoleName: "Risk Sharing",
                            attrValueAsString: account.udts.riskSharing?.value ?? '',
                        },
                    ],
                    userDefinedTermReference: {
                        ID: "Risk Sharing",
                    },
                });
            }
            if (account.udts?.enableChannelPartner) {
                udts.push({
                    udtListValueSet: [
                        {
                            attributeRoleName: "Channel Partner",
                            attrValueAsString: account.udts.channelPartner?.value ?? '',
                        },
                    ],
                    userDefinedTermReference: {
                        ID: "Channel Partner",
                    },
                });
            }

            return {
                asOfDate: getDateString(draft.asOfDate),
                propagate: draft.updateAllVersions,
                maintenanceInfo: {
                    transactionType: draft.action
                },
                maintenanceReasonCode: {
                    codeSetName: isCreateAction ? 'AccountCreateReason' : 'AccountAmendReason',
                    shortName: isCreateAction ? 'Account create' : 'Health Care Company Request - Other'
                },
                maintenanceComment: `Updated by ${email} using business tool`,
                account: {
                    ...(isCreateAction ? {} : { accountIdentifier: account.accountIdentifier }),
                    accountHccIdentifier: account.accountHccId,
                    organization: {
                        name: account.accountName,
                        taxID: draft.taxEntity.value
                    },
                    typeOfAccount: {
                        codeSetName: 'AccountType',
                        codeEntry: account.accountType.value
                    },
                    canHaveSubscriptions: account.subscriptionsAllowed,
                    effectiveStartDate: draft.effectiveDate,
                    ...(udts.length > 0 && { accountUDTList: udts }),
                    ...(!account.billParent && { billingCycleIdentifier: account.billingCycle.label }),
                    accountAnniversaryDate: {
                        firstRenewalDate: draft.effectiveDate,
                        renewalInterval: draft.renewalInterval
                    },
                    generalCorrespondenceAddress: {
                        addressInformation: {
                            postalAddress: {
                                address: account.primaryAddress1,
                                address2: account.primaryAddress2 ?? '',
                                address3: account.primaryAddress3 ?? '',
                                stateCode: account.primaryAddressState.value,
                                zipCode: account.primaryAddressZip,
                                cityName: account.primaryAddressCity,
                                ignoreAddressCheck: account.ignorePrimaryAddressCheck
                            }
                        },
                        correspondenceUsageType: {
                            codeSetName: 'correspondenceUsageType',
                            shortName: 'Correspondence'
                        }
                    },
                    ...(!account.billParent && {
                        otherCorrespondenceAddressList: {
                            otherCorrespondenceAddress: {
                                addressInformation: {
                                    postalAddress: {
                                        address: account.billingAddress1,
                                        address2: account.billingAddress2 ?? '',
                                        address3: account.billingAddress3 ?? '',
                                        stateCode: account.billingAddressState.value,
                                        zipCode: account.billingAddressZip,
                                        cityName: account.billingAddressCity,
                                        ignoreAddressCheck: account.ignoreBillingAddressCheck
                                    }
                                },
                                correspondenceUsageType: {
                                    codeSetName: 'AccountCorrespondenceUsage',
                                    shortName: 'Premium Billing'
                                }
                            }
                        }
                    }),
                    ...(account.parentAccount && {
                        topAccount: {
                            accountHccIdentifier: draft.accountHccId
                        },
                        parentAccount: {
                            accountHccIdentifier: account.parentAccount.value
                        }
                    }),
                    ...(account.billParent !== undefined && { billParent: account.billParent }),
                }
            };
        });

        try {
            const batchJobResponse = await submitAccountBatchJob({ bodies });
            const pk = batchJobResponse?.pk;

            if (!pk) {
                throw new Error('No pk returned from batch job');
            }

            const sk = batchJobResponse?.sk || 'SUBMIT_ACCOUNT_BATCH';

            let requestStatus = 'PENDING';
            let jobStatusResponse;

            while (requestStatus === 'PENDING') {
                await delay(3000);
                jobStatusResponse = await fetchGetApiRequestJobStatus({ pk, sk });
                requestStatus = jobStatusResponse?.requestStatus;
            }

            if (requestStatus === 'ERROR') {
                toast.error('Batch job failed!', {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: true,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light",
                    transition: Bounce,
                });
                setIsSubmitting(false);
                return;
            }

            const responses = jobStatusResponse?.responses || [];
            if (responses.some(r => r?.status === 'FAILURE' || r?.status === 'ERROR')) {
                toast.error('Some accounts failed to submit!', {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: true,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light",
                    transition: Bounce,
                });
                setIsSubmitting(false);
                return;
            }

            await submitV2Audit({
                entityType: 'ACCOUNT',
                entityId: draft.accountHccId,
                action: draft.action,
                email,
                request: bodies,
                response: responses
            });

            removeAccountByAccountId(draft.accountId);
            toast.success('Success!', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
                transition: Bounce,
            });

            navigate(`/config/account-builder`);
        } catch (error) {
            console.error('Account submission error:', error);
            toast.error('Something went wrong!', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
                transition: Bounce,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={'container'} style={{ padding: '1rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div>
                    <h4>As Of Date</h4>
                    <p>{getDateString(draft.asOfDate)}</p>
                    {errors.asOfDate && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{errors.asOfDate.message}</p>}
                </div>
                <div>
                    <h4>Update All Versions</h4>
                    <p>{draft.updateAllVersions ? 'YES' : 'NO'}</p>
                </div>
                <div>
                    <h4>Transaction Type</h4>
                    <p>{draft.action.toUpperCase()}</p>
                </div>
                <div>
                    <h4>Tax Entity</h4>
                    <p>{draft.taxEntity?.label ?? ''}</p>
                    {errors.taxEntity && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{errors.taxEntity.message}</p>}
                </div>
                <div>
                    <h4>Account Name</h4>
                    <p>{draft.accountName ?? ''}</p>
                    {errors.accountName && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{errors.accountName.message}</p>}
                </div>
                <div>
                    <h4>Account HCC ID</h4>
                    <p>{draft.accountHccId ?? ''}</p>
                    {errors.accountHccId && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{errors.accountHccId.message}</p>}
                </div>
                <div>
                    <h4>Account Type</h4>
                    <p>{draft.accountType?.label ?? ''}</p>
                    {errors.accountType && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{errors.accountType.message}</p>}
                </div>
                <div>
                    <h4>Effective Date</h4>
                    <p>{draft.effectiveDate ?? ''}</p>
                    {errors.effectiveDate && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{errors.effectiveDate.message}</p>}
                </div>
                <div>
                    <h4>Renewal Interval</h4>
                    <p>{draft.renewalInterval ?? ''}</p>
                    {errors.renewalInterval && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{errors.renewalInterval.message}</p>}
                </div>
                <div>
                    <h4>Subscriptions Allowed</h4>
                    <p>{draft.subscriptionsAllowed ? 'YES' : 'NO'}</p>
                </div>
                <div>
                    <h4>Primary Correspondence Address</h4>
                    <p>{draft.primaryAddress1 ?? ''}</p>
                    {draft.primaryAddress2 && <p>{draft.primaryAddress2}</p>}
                    {draft.primaryAddress3 && <p>{draft.primaryAddress3}</p>}
                    {draft.primaryAddressCity && draft.primaryAddressState?.value && draft.primaryAddressZip &&
                        <p>{`${draft.primaryAddressCity}, ${draft.primaryAddressState.value} ${draft.primaryAddressZip}`}</p>
                    }
                    {errors.primaryAddress1 && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{errors.primaryAddress1.message}</p>}
                    {errors.primaryAddressCity && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{errors.primaryAddressCity.message}</p>}
                    {errors.primaryAddressState && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{errors.primaryAddressState.message}</p>}
                    {errors.primaryAddressZip && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{errors.primaryAddressZip.message}</p>}
                    <br />
                    <p>{`Ignore Address Check: ${draft.ignorePrimaryAddressCheck ? 'YES' : 'NO'}`}</p>
                </div>
                <div>
                    <h4>Billing Cycle Name</h4>
                    <p>{draft.billingCycle?.label ?? ''}</p>
                    {errors.billingCycle && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{errors.billingCycle.message}</p>}
                </div>
                <div>
                    <h4>Premium Billing Address</h4>
                    <p>{draft.billingAddress1 ?? ''}</p>
                    {draft.billingAddress2 && <p>{draft.billingAddress2}</p>}
                    {draft.billingAddress3 && <p>{draft.billingAddress3}</p>}
                    {draft.billingAddressCity && draft.billingAddressState?.value && draft.billingAddressZip &&
                        <p>{`${draft.billingAddressCity}, ${draft.billingAddressState.value} ${draft.billingAddressZip}`}</p>
                    }
                    {errors.billingAddress1 && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{errors.billingAddress1.message}</p>}
                    {errors.billingAddressCity && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{errors.billingAddressCity.message}</p>}
                    {errors.billingAddressState && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{errors.billingAddressState.message}</p>}
                    {errors.billingAddressZip && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{errors.billingAddressZip.message}</p>}
                    <br />
                    <p>{`Ignore Address Check: ${draft.ignoreBillingAddressCheck ? 'YES' : 'NO'}`}</p>
                </div>
                <div>
                    <h4>Sub Accounts</h4>
                    {draft.subAccounts.length === 0 && <p>None</p>}
                    {draft.subAccounts.map(subAccount => <div key={subAccount.accountId}><p>{`${subAccount.accountName} (${subAccount.accountHccId})`}</p></div>)}
                </div>
                {draft.udts.enableInvoiceInformation && (
                    <div>
                        <h4>Invoice Information</h4>
                        <p>{`Invoice Type: ${draft.udts.invoiceType?.label ?? ''}`}</p>
                        <p>{`Payment Remit: ${draft.udts.paymentRemit?.label ?? ''}`}</p>
                        <p>{`Purchase Order: ${draft.udts.purchaseOrder ?? ''}`}</p>
                        {errors.udts?.invoiceType && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{errors.udts.invoiceType.message}</p>}
                        {errors.udts?.paymentRemit && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{errors.udts.paymentRemit.message}</p>}
                        {errors.udts?.purchaseOrder && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{errors.udts.purchaseOrder.message}</p>}
                    </div>
                )}
                {draft.udts.enableFundingMode && (
                    <div>
                        <h4>Funding Mode</h4>
                        <p>{`Funding Mode: ${draft.udts.fundingMode?.label ?? ''}`}</p>
                        {errors.udts?.fundingMode && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{errors.udts.fundingMode.message}</p>}
                    </div>
                )}
                {draft.udts.enableAccountMigrationDate && (
                    <div>
                        <h4>Account Migration Date</h4>
                        <p>{`Account Migration Date: ${convertDateStrToUDTDateStr(draft.udts.accountMigrationDate)}`}</p>
                        {errors.udts?.accountMigrationDate && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{errors.udts.accountMigrationDate.message}</p>}
                    </div>
                )}
                {draft.udts.enableNysHcra && (
                    <div>
                        <h4>New York State HCRA Election</h4>
                        <p>{`Election Type: ${draft.udts.nysHcra?.label ?? ''}`}</p>
                        <p>{`Election Date: ${convertDateStrToUDTDateStr(draft.udts.nysHcraDate)}`}</p>
                        <p>{`Election Notes: ${draft.udts.nysHcraNotes ?? ''}`}</p>
                        {errors.udts?.nysHcra && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{errors.udts.nysHcra.message}</p>}
                        {errors.udts?.nysHcraDate && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{errors.udts.nysHcraDate.message}</p>}
                        {errors.udts?.nysHcraNotes && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{errors.udts.nysHcraNotes.message}</p>}
                    </div>
                )}
                {draft.udts.enableRiskSharing && (
                    <div>
                        <h4>Risk Sharing</h4>
                        <p>{`Enable Risk Sharing: ${draft.udts.riskSharing?.label ?? ''}`}</p>
                        {errors.udts?.riskSharing && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{errors.udts.riskSharing.message}</p>}
                    </div>
                )}
                {draft.udts.enableChannelPartner && (
                    <div>
                        <h4>Channel Partner</h4>
                        <p>{`Channel Partner: ${draft.udts.channelPartner?.label ?? ''}`}</p>
                        {errors.udts?.channelPartner && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{errors.udts.channelPartner.message}</p>}
                    </div>
                )}
            </div>
            <div style={{ display: 'flex', gap: '1rem', padding: '2rem 1rem 1rem 0rem' }}>
                <button type="button" className="btn edit-btn" disabled={!isValid || isSubmitting} onClick={onSubmitAccount}>
                    {isSubmitting ? 'Submitting...' : 'Submit Account'}
                </button>
            </div>
            <p className="sub-title">*Note: this action will submit the Top Account and all of its Sub Accounts, if any, to HRP.</p>
            <br />
        </div>
    );
};

export default TopAccountStepSix;
